import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getHQCredentials, getScoutCredentials, saveScoutApplication, setScoutSession } from "./ScoutAuth";
import matrixosLogo from "./matrixos-logo-dark.png";
import scoutHero from "./matrixos-scout-hero.png";


function PublicScoutApplication({ onBack }) {
  const [step, setStep] = useState("landing");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    region: "",
    age: "",
    occupation: "",
    linkedIn: "",
    motivation: "",
    experience: "",
  });
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [step]);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const formReady = form.name.trim() && form.email.trim() && form.phone.trim() && form.country.trim() && form.region.trim() && form.motivation.trim() && form.experience.trim();

  const questions = [
    { id: "q1", label: "Motivation", question: "Why do you want to become an Operational Scout?", limit: 500, type: "text" },
    { id: "q2", label: "Observation", question: "Tell us about a time you noticed a problem that others overlooked. What was the problem and what did you do?", limit: 500, type: "text" },
    { id: "q3", label: "Approach", question: "Imagine you are asked to visit five businesses in three days. How would you approach the first business?", limit: 500, type: "text" },
    { id: "q4", label: "Professionalism", question: "A business manager tells you they only have two minutes to speak. What would you do?", limit: 500, type: "text" },
    { id: "q5", label: "Information", question: "What information do you believe is important when identifying an opportunity for MatrixOS?", limit: 500, type: "text" },
    { id: "q6", label: "Mindset", question: "What do you think makes someone successful in a contribution-based opportunity?", limit: 500, type: "text" },
    { id: "q7", label: "Communication", question: "Describe a situation where you had to speak to someone you did not know. How did it go?", limit: 500, type: "text" },
    { id: "q8", label: "Resilience", question: "What would you do if a business owner rejected your approach?", limit: 500, type: "text" },
    {
      id: "q9",
      label: "Current Situation",
      question: "Which best describes your current situation?",
      type: "choice",
      options: ["Full-time employed", "Part-time employed", "Self-employed", "Student", "Unemployed", "Other"],
    },
    { id: "q10", label: "Why You?", question: "Why should MatrixOS select you over another applicant?", limit: 500, type: "text" },
    {
      id: "q11",
      label: "Commitment",
      question: "If selected for the next stage, are you willing to visit at least 3 businesses and submit observations as part of your practical assessment?",
      type: "choice",
      options: ["Yes", "No"],
    },
  ];

  const answeredCount = questions.filter((q) => {
    const value = answers[q.id];
    return typeof value === "string" ? value.trim().length > 0 : value !== undefined && value !== null;
  }).length;

  const letterCount = (value) => String(value || "").length;

  const screeningReady = answeredCount === questions.length;

  function submitScreening(e) {
    e.preventDefault();
    if (!screeningReady) return;
    const saved = saveScoutApplication({
      ...form,
      screeningAnswers: answers,
      stage: "Screening Submitted",
      score: null,
      signal: "Awaiting HQ review",
      practical: "Locked until interview pass",
      notes: "Application and screening questions submitted. HQ should review written answers and decide whether to schedule an interview.",
      applied: "Just now",
    });
    setResult(saved);
    setStep("submitted");
  }

  const earnings = [
    ["10 Site Operator", "R16,990/month", "R1,699/month", "R20,388"],
    ["50 Site Operator", "R84,950/month", "R8,495/month", "R101,940"],
    ["100 Site Operator", "R169,900/month", "R16,990/month", "R203,880"],
  ];

  if (step === "submitted") {
    return (
      <div className="min-h-screen bg-[#061427] text-white">
        <div className="pointer-events-none fixed inset-0 opacity-80">
          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,.04)_1px,transparent_1px),linear-gradient(180deg,rgba(255,255,255,.04)_1px,transparent_1px)] bg-[size:72px_72px]" />
          <div className="absolute -left-32 top-8 h-96 w-96 rounded-full bg-cyan-400/20 blur-3xl" />
          <div className="absolute right-0 top-0 h-[32rem] w-[32rem] rounded-full bg-blue-500/20 blur-3xl" />
        </div>
        <main className="relative z-10 mx-auto grid min-h-screen max-w-3xl place-items-center px-6 py-12">
          <section className="rounded-[2rem] border border-white/15 bg-slate-950/75 p-8 text-center shadow-[0_30px_90px_rgba(0,0,0,.45)] backdrop-blur-xl">
            <div className="text-xs font-black uppercase tracking-[0.36em] text-cyan-200">Screening Submitted</div>
            <h1 className="mt-4 text-4xl font-black tracking-[-0.06em]">Your application is now with MatrixOS HQ.</h1>
            <p className="mt-4 leading-7 text-slate-300">Your application and written screening answers were added to the HQ Applications pipeline.</p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="rounded-3xl bg-white/[0.06] p-5">
                <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">Status</div>
                <div className="mt-2 text-xl font-black text-cyan-100">Awaiting HQ Review</div>
              </div>
              <div className="rounded-3xl bg-white/[0.06] p-5">
                <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">Next Step</div>
                <div className="mt-2 text-sm font-bold text-slate-200">HQ review and interview decision</div>
              </div>
            </div>
            <button type="button" onClick={onBack} className="mt-7 rounded-2xl bg-gradient-to-r from-cyan-300 via-sky-400 to-blue-500 px-7 py-4 font-black text-slate-950">Back to Login</button>
          </section>
        </main>
      </div>
    );
  }

  if (step === "form") {
    return (
      <div className="min-h-screen bg-[#061427] text-white">
        <div className="pointer-events-none fixed inset-0 opacity-80">
          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,.04)_1px,transparent_1px),linear-gradient(180deg,rgba(255,255,255,.04)_1px,transparent_1px)] bg-[size:72px_72px]" />
          <div className="absolute -left-32 top-8 h-96 w-96 rounded-full bg-cyan-400/20 blur-3xl" />
          <div className="absolute right-0 top-0 h-[32rem] w-[32rem] rounded-full bg-blue-500/20 blur-3xl" />
        </div>
        <header className="relative z-10 mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-6 py-4">
          <div>
            <img src={matrixosLogo} alt="MatrixOS" className="h-40 w-auto" />
            <div className="mt-1 text-xs font-black uppercase tracking-[0.35em] text-cyan-200">Scout Application</div>
          </div>
          <button type="button" onClick={() => setStep("landing")} className="rounded-full border border-white/15 px-5 py-2 text-xs font-black uppercase tracking-[0.18em] text-white/80 hover:bg-white/10">← Back to Landing</button>
        </header>
        <main className="relative z-10 mx-auto max-w-6xl px-6 pb-14">
          <section className="rounded-[2.4rem] border border-white/15 bg-slate-950/75 p-7 shadow-[0_30px_90px_rgba(0,0,0,.45)] backdrop-blur-xl sm:p-9">
            <div className="grid gap-8 lg:grid-cols-[.75fr_1.25fr]">
              <div>
                <div className="text-xs font-black uppercase tracking-[0.36em] text-cyan-200">Step 1 of 2</div>
                <h1 className="mt-4 text-5xl font-black tracking-[-0.07em]">Apply to become an Operational Scout</h1>
                <p className="mt-5 leading-8 text-slate-300">After this form you will go straight into screening questions. HQ uses your application and screening responses to decide whether to interview you.</p>
                <div className="mt-6 rounded-3xl border border-cyan-200/15 bg-cyan-300/10 p-5 text-sm leading-7 text-cyan-50">
                  Practical assessment comes after the interview. Training comes after practical approval.
                </div>
              </div>
              <form onSubmit={(e) => { e.preventDefault(); if (formReady) { setStep("screening"); window.scrollTo({ top: 0, behavior: "smooth" }); } }} className="grid gap-4 sm:grid-cols-2">
                <input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Full name" className="rounded-2xl border border-white/12 bg-slate-900/80 px-5 py-4 outline-none focus:border-cyan-300" required />
                <input value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="Email address" type="email" className="rounded-2xl border border-white/12 bg-slate-900/80 px-5 py-4 outline-none focus:border-cyan-300" required />
                <input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="Phone / WhatsApp" className="rounded-2xl border border-white/12 bg-slate-900/80 px-5 py-4 outline-none focus:border-cyan-300" required />
                <input value={form.age} onChange={(e) => update("age", e.target.value)} placeholder="Age" className="rounded-2xl border border-white/12 bg-slate-900/80 px-5 py-4 outline-none focus:border-cyan-300" />
                <input value={form.country} onChange={(e) => update("country", e.target.value)} placeholder="Country" className="rounded-2xl border border-white/12 bg-slate-900/80 px-5 py-4 outline-none focus:border-cyan-300" required />
                <input value={form.region} onChange={(e) => update("region", e.target.value)} placeholder="City / region" className="rounded-2xl border border-white/12 bg-slate-900/80 px-5 py-4 outline-none focus:border-cyan-300" required />
                <input value={form.occupation} onChange={(e) => update("occupation", e.target.value)} placeholder="Current occupation" className="rounded-2xl border border-white/12 bg-slate-900/80 px-5 py-4 outline-none focus:border-cyan-300" />
                <input value={form.linkedIn} onChange={(e) => update("linkedIn", e.target.value)} placeholder="LinkedIn profile (optional)" className="rounded-2xl border border-white/12 bg-slate-900/80 px-5 py-4 outline-none focus:border-cyan-300" />
                <textarea value={form.motivation} onChange={(e) => update("motivation", e.target.value)} placeholder="Why do you want to become an Operational Scout?" className="min-h-[120px] rounded-2xl border border-white/12 bg-slate-900/80 px-5 py-4 outline-none focus:border-cyan-300 sm:col-span-2" required />
                <textarea value={form.experience} onChange={(e) => update("experience", e.target.value)} placeholder="Tell us about your experience in sales, operations, retail, hospitality, warehousing, or management." className="min-h-[120px] rounded-2xl border border-white/12 bg-slate-900/80 px-5 py-4 outline-none focus:border-cyan-300 sm:col-span-2" required />
                <button disabled={!formReady} className="rounded-2xl bg-gradient-to-r from-cyan-300 via-sky-400 to-blue-500 px-6 py-4 font-black text-slate-950 disabled:opacity-50 sm:col-span-2">Continue to Screening Questions</button>
              </form>
            </div>
          </section>
        </main>
      </div>
    );
  }

  if (step === "screening") {
    return (
      <div className="min-h-screen bg-[#061427] text-white">
        <div className="pointer-events-none fixed inset-0 opacity-80">
          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,.04)_1px,transparent_1px),linear-gradient(180deg,rgba(255,255,255,.04)_1px,transparent_1px)] bg-[size:72px_72px]" />
          <div className="absolute -left-32 top-8 h-96 w-96 rounded-full bg-cyan-400/20 blur-3xl" />
          <div className="absolute right-0 top-0 h-[32rem] w-[32rem] rounded-full bg-blue-500/20 blur-3xl" />
        </div>
        <header className="relative z-10 mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-4 px-6 py-4">
          <div>
            <img src={matrixosLogo} alt="MatrixOS" className="h-40 w-auto" />
            <div className="mt-1 text-xs font-black uppercase tracking-[0.35em] text-cyan-200">Screening Questions</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="rounded-full border border-cyan-200/20 bg-white/[0.04] px-5 py-2 text-sm font-black text-cyan-100">{answeredCount}/{questions.length} answered</div>
            <button type="button" onClick={() => setStep("form")} className="rounded-full border border-white/15 px-5 py-2 text-xs font-black uppercase tracking-[0.18em] text-white/80 hover:bg-white/10">← Back to Application</button>
          </div>
        </header>
        <main className="relative z-10 mx-auto max-w-5xl px-6 pb-14">
          <section className="rounded-[2.4rem] border border-white/15 bg-slate-950/75 p-7 shadow-[0_30px_90px_rgba(0,0,0,.45)] backdrop-blur-xl sm:p-9">
            <div className="text-xs font-black uppercase tracking-[0.36em] text-cyan-200">Step 2 of 2</div>
            <h1 className="mt-4 text-5xl font-black tracking-[-0.07em]">How do you think?</h1>
            <p className="mt-4 max-w-3xl leading-8 text-slate-300">These questions help MatrixOS HQ understand your curiosity, judgement, and discovery mindset before the interview stage.</p>
            <form onSubmit={submitScreening} className="mt-8 space-y-5">
              {questions.map((q, idx) => (
                <div key={q.id} className="rounded-3xl border border-white/12 bg-white/[0.04] p-5">
                  <div className="flex gap-3">
                    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-cyan-300 font-black text-slate-950">{idx + 1}</div>
                    <div className="font-black text-white">{q.question}</div>
                  </div>
                  {q.type === "choice" ? (
                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                      {q.options.map((option) => (
                        <label key={option} className={`cursor-pointer rounded-2xl border p-4 text-sm font-bold transition ${answers[q.id] === option ? "border-cyan-300 bg-cyan-300/15 text-cyan-50" : "border-white/10 bg-slate-900/55 text-slate-300 hover:border-cyan-300/50"}`}>
                          <input type="radio" name={q.id} value={option} checked={answers[q.id] === option} onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))} className="mr-3" />
                          {option}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-4">
                      <textarea
                        value={answers[q.id] || ""}
                        onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                        placeholder={`Answer in ${q.limit} letters or less...`}
                        className="min-h-[150px] w-full rounded-2xl border border-white/12 bg-slate-900/65 px-5 py-4 text-slate-100 outline-none focus:border-cyan-300"
                        required
                      />
                      <div className={`mt-2 text-right text-xs font-bold ${letterCount(answers[q.id]) > q.limit ? "text-red-300" : "text-slate-500"}`}>{letterCount(answers[q.id])}/{q.limit} letters</div>
                    </div>
                  )}
                </div>
              ))}
              <div className="sticky bottom-4 rounded-3xl border border-white/15 bg-slate-950/90 p-4 shadow-2xl backdrop-blur-xl">
                <button disabled={!screeningReady} className="w-full rounded-2xl bg-gradient-to-r from-cyan-300 via-sky-400 to-blue-500 px-6 py-4 font-black text-slate-950 disabled:opacity-50">Submit Screening</button>
              </div>
            </form>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-hidden bg-[#050b18] text-white">
      <div className="pointer-events-none fixed inset-0 opacity-90">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_8%,rgba(54,144,255,.30),transparent_34%),linear-gradient(90deg,rgba(255,255,255,.045)_1px,transparent_1px),linear-gradient(180deg,rgba(255,255,255,.045)_1px,transparent_1px)] bg-[size:auto,72px_72px,72px_72px]" />
        <div className="absolute -left-32 top-8 h-96 w-96 rounded-full bg-cyan-400/20 blur-3xl" />
        <div className="absolute right-0 top-0 h-[42rem] w-[42rem] rounded-full bg-blue-500/20 blur-3xl" />
      </div>

      <header className="relative z-30 mx-auto flex max-w-[1500px] items-center justify-between gap-6 px-7 py-3 sm:px-10 lg:px-12">
        <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="block">
          <img src={matrixosLogo} alt="MatrixOS" className="h-48 w-auto sm:h-56" />
        </button>
        <nav className="hidden items-center gap-3 md:flex">
          {[
            ["The Opportunity", "opportunity"],
            ["The Role", "role"],
            ["Your Growth", "growth"],
            ["The Process", "process"],
          ].map(([label, target]) => (
            <button key={target} type="button" onClick={() => document.getElementById(target)?.scrollIntoView({ behavior: "smooth" })} className="rounded-2xl border border-white/10 bg-white/[0.035] px-4 py-3 text-[11px] font-black uppercase tracking-[0.18em] text-white/80 transition hover:border-blue-300/40 hover:bg-blue-400/10 hover:text-blue-200">
              {label}
            </button>
          ))}
          <button type="button" onClick={onBack} className="rounded-2xl border border-blue-300/40 bg-blue-500 px-5 py-3 text-[11px] font-black uppercase tracking-[0.18em] text-white shadow-[0_0_28px_rgba(59,130,246,.45)] hover:bg-blue-400">Login</button>
        </nav>
      </header>

      <main className="relative z-10">
        <section id="opportunity" className="relative mx-auto min-h-[700px] max-w-[1500px] px-7 pb-16 pt-0 sm:px-10 lg:px-12">
          <div className="absolute inset-y-0 right-0 hidden w-[58%] overflow-hidden lg:block">
            <img src={scoutHero} alt="Operational Scout in warehouse" className="absolute inset-0 h-full w-full object-cover opacity-95" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_55%_38%,transparent_0%,rgba(5,11,24,.04)_34%,rgba(5,11,24,.75)_78%),linear-gradient(90deg,#050b18_0%,rgba(5,11,24,.80)_18%,rgba(5,11,24,.20)_52%,#050b18_100%),linear-gradient(180deg,#050b18_0%,transparent_20%,transparent_70%,#050b18_100%)]" />
          </div>

          <div className="relative z-10 max-w-[760px]">
            <h1 className="mt-3 text-5xl font-black uppercase leading-[0.92] tracking-[-0.07em] text-white sm:text-6xl lg:text-[6.8rem]">
              Operational <span className="block bg-gradient-to-r from-blue-500 via-sky-400 to-cyan-200 bg-clip-text text-transparent">Scouts</span>
            </h1>
            <h2 className="mt-5 max-w-2xl text-3xl font-black leading-[1.02] tracking-[-0.05em] text-white sm:text-4xl lg:text-5xl">
              The future leaders aren't hired. <span className="block text-blue-300">They're discovered.</span>
            </h2>

            <div className="mt-8 flex flex-wrap gap-4 text-xs font-black uppercase tracking-[0.12em] text-white/90">
              <div className="flex items-center gap-3 rounded-2xl border border-blue-300/20 bg-white/[0.035] px-4 py-3"><span className="grid h-10 w-10 place-items-center rounded-full border border-blue-400/50 text-blue-300">◎</span>The Truth</div>
              <div className="flex items-center gap-3 rounded-2xl border border-blue-300/20 bg-white/[0.035] px-4 py-3"><span className="grid h-10 w-10 place-items-center rounded-full border border-blue-400/50 text-blue-300">⚙</span>Influence<br />Operations</div>
              <div className="flex items-center gap-3 rounded-2xl border border-blue-300/20 bg-white/[0.035] px-4 py-3"><span className="grid h-10 w-10 place-items-center rounded-full border border-blue-400/50 text-blue-300">▥</span>Improve<br />Every Day</div>
            </div>

            <p className="mt-9 max-w-2xl text-xl leading-9 text-slate-200">We are building a new kind of operational team. We are looking for curious, professional and reliable people who can enter real businesses, observe what is happening, identify operational problems and help MatrixOS create clarity.</p>
            <p className="mt-5 text-xl font-black text-blue-300">This is not a sales job. This is a discovery role.</p>
          </div>

          <div className="relative z-10 mt-14 max-w-[1220px] rounded-[1.7rem] border border-blue-400/55 bg-blue-950/30 p-6 shadow-[0_20px_70px_rgba(0,0,0,.35)] sm:p-8">
            <div className="grid gap-8 md:grid-cols-[280px_1fr_.85fr] md:items-center">
              <div className="flex items-center gap-5">
                <div className="grid h-20 w-20 shrink-0 place-items-center rounded-full border border-blue-400/35 bg-blue-500/15 text-5xl text-blue-300">◎</div>
                <div>
                  <div className="text-sm font-black uppercase tracking-[0.16em] text-blue-300">Your Mission</div>
                  <div className="mt-2 text-3xl font-black uppercase leading-[1.05] text-white">Discover.<br />Understand.<br />Create Clarity.</div>
                </div>
              </div>
              <div className="border-blue-400/30 text-lg leading-8 text-slate-200 md:border-l md:pl-9">
                Your job is not to convince. <b className="text-blue-300">Your job is to understand.</b><br />You are not selling software. You are helping businesses see what they could not see before.
              </div>
              <div className="hidden min-h-[120px] rounded-3xl bg-[radial-gradient(circle_at_40%_50%,rgba(45,142,255,.45),transparent_30%),linear-gradient(90deg,transparent,rgba(54,144,255,.16))] md:block" />
            </div>
          </div>
        </section>

        <section id="role" className="bg-[#f7fbff] px-7 py-20 text-slate-950 sm:px-10 lg:px-12">
          <div className="mx-auto max-w-[1500px]">
            <div className="grid gap-0 overflow-hidden rounded-[2.2rem] bg-white shadow-[0_30px_90px_rgba(15,23,42,.12)] lg:grid-cols-4">
              {[
                ["◎", "What is an Operational Scout?", "An Operational Scout identifies operational challenges inside businesses and reports valuable opportunities to MatrixOS.", ["Observe real operations", "Ask better questions", "Identify hidden problems", "Capture useful information", "Build relationships", "Submit opportunities to MatrixOS"]],
                ["✕", "This is not for everyone", "This is not cold calling, door-to-door pressure selling, chasing quotas or reading scripts.", ["Curious", "Reliable", "Professional", "Comfortable speaking to people", "Willing to learn", "Good at listening", "Serious about growth"]],
                ["◇", "Why this opportunity matters", "Most companies reward time. MatrixOS rewards contribution. Your growth is connected to the value you create.", ["Operational Scout", "Senior Scout", "Growth Partner", "Regional Leader", "National Leadership"]],
                ["$", "How you can earn", "Operational Scouts earn from customers they help bring into MatrixOS. Commission can continue for up to 12 months.", ["MatrixOS Bundle: R1,699/site/month", "10 sites = R16,990/month", "50 sites = R84,950/month", "100 sites = R169,900/month"]],
              ].map(([icon, title, body, list]) => (
                <article key={title} className="border-slate-200 p-8 lg:border-r last:border-r-0 xl:p-10">
                  <div className="grid h-16 w-16 place-items-center rounded-full bg-blue-50 text-3xl text-blue-500">{icon}</div>
                  <h3 className="mt-8 text-2xl font-black uppercase leading-tight tracking-[-0.04em]">{title}</h3>
                  <p className="mt-6 min-h-[96px] text-base leading-8 text-slate-600">{body}</p>
                  <div className="mt-6 space-y-4 text-base text-slate-700">
                    {list.map((item) => <div key={item} className="flex gap-3"><span className="font-black text-blue-500">✓</span><span>{item}</span></div>)}
                  </div>
                </article>
              ))}
            </div>

            <div id="growth" className="mt-16 rounded-[2.2rem] border border-slate-200 bg-white p-8 shadow-[0_25px_70px_rgba(15,23,42,.10)] lg:p-12">
              <div className="grid gap-10 lg:grid-cols-[.9fr_1.1fr] lg:items-start">
                <div>
                  <div className="text-xs font-black uppercase tracking-[0.42em] text-blue-500">Earnings Model</div>
                  <h2 className="mt-5 text-5xl font-bold leading-[1.05] tracking-[-0.05em] text-slate-950 lg:text-6xl">Target multi-site operators. That is where the numbers change.</h2>
                  <p className="mt-7 text-lg leading-8 text-slate-600">MatrixOS Bundle: <b className="text-slate-950">R1,699 per site per month.</b> Scout contribution: <b className="text-slate-950">10% of monthly client value for 12 months.</b></p>
                </div>
                <div className="overflow-hidden rounded-3xl border border-slate-200">
                  <div className="grid grid-cols-4 bg-slate-950 px-5 py-4 text-xs font-black uppercase tracking-[0.18em] text-cyan-200">
                    <div>Operator</div><div>Client Value</div><div>Scout / Month</div><div>12 Month Potential</div>
                  </div>
                  {earnings.map(([name, client, scout, potential], idx) => (
                    <div key={name} className={`grid grid-cols-4 gap-3 px-5 py-5 text-sm ${idx % 2 ? "bg-slate-50" : "bg-white"}`}>
                      <div className="font-black text-slate-950">{name}</div>
                      <div className="font-bold text-slate-700">{client}</div>
                      <div className="rounded-2xl bg-gradient-to-r from-cyan-300 to-blue-400 px-3 py-2 font-black text-slate-950">{scout}</div>
                      <div className="font-black text-slate-950">{potential}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-8 rounded-3xl border border-blue-100 bg-blue-50 px-6 py-5 text-slate-700"><b className="text-slate-950">MatrixOS rewards contribution, not time.</b> One qualified multi-site operator can create recurring monthly earnings for up to 12 months.</div>
            </div>
          </div>
        </section>

        <section className="bg-[#050b18] px-7 py-20 text-white sm:px-10 lg:px-12">
          <div className="mx-auto grid max-w-[1500px] gap-8 lg:grid-cols-2">
            <div className="rounded-[2rem] border border-red-300/35 bg-red-400/8 p-8">
              <h3 className="text-3xl font-black uppercase tracking-[-0.04em] text-red-200">Who should not apply?</h3>
              <div className="mt-7 space-y-4">
                {[
                  "You are uncomfortable speaking to strangers.",
                  "You avoid difficult conversations.",
                  "You expect MatrixOS to find opportunities for you.",
                  "You give up after hearing no.",
                  "You struggle to complete assignments on time.",
                ].map((item) => <div key={item} className="rounded-2xl bg-red-400/10 px-5 py-4 text-red-50">× {item}</div>)}
              </div>
            </div>
            <div className="rounded-[2rem] border border-blue-300/40 bg-blue-400/8 p-8">
              <h3 className="text-3xl font-black uppercase tracking-[-0.04em] text-blue-300">Apply if you are...</h3>
              <div className="mt-7 space-y-4">
                {[
                  "Naturally curious about how businesses work.",
                  "Comfortable approaching people professionally.",
                  "Reliable without being chased.",
                  "Willing to learn and improve.",
                  "Serious about growth.",
                  "Interested in operations and problem solving.",
                ].map((item) => <div key={item} className="rounded-2xl bg-blue-300/10 px-5 py-4 text-blue-50">✓ {item}</div>)}
              </div>
            </div>
          </div>
        </section>

        <section id="process" className="bg-[#050b18] px-7 pb-20 text-white sm:px-10 lg:px-12">
          <div className="mx-auto grid max-w-[1500px] gap-8 lg:grid-cols-[.8fr_1.2fr]">
            <div className="rounded-[2.2rem] border border-white/20 bg-slate-950/70 p-8 shadow-[0_25px_80px_rgba(0,0,0,.35)]">
              <div className="text-xs font-black uppercase tracking-[0.42em] text-cyan-200">Recruitment Process</div>
              <h2 className="mt-5 text-4xl font-black tracking-[-0.06em]">How the process works</h2>
              <p className="mt-4 text-slate-400">We follow a structured process to find people brave enough to approach real businesses and disciplined enough to learn.</p>
              <div className="mt-8 space-y-4">
                {[
                  ["①", "Apply", "Submit your details and why you are interested."],
                  ["②", "Screening Questions", "We assess communication, attitude, curiosity and effort."],
                  ["③", "Interview", "HQ assesses mindset, professionalism and ability to learn."],
                  ["④", "Practical Assessment", "Visit 5 businesses in 3 days and submit observations."],
                  ["⑤", "Selection", "If successful, you receive scout access and begin training."],
                ].map(([icon, title, body]) => (
                  <div key={title} className="flex gap-4 rounded-3xl border border-white/10 bg-white/[0.04] p-5">
                    <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-blue-500 text-xl font-black text-white">{icon}</div>
                    <div><div className="text-xl font-black text-white">{title}</div><div className="mt-1 text-sm leading-7 text-slate-400">{body}</div></div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[2.2rem] border border-white/20 bg-slate-950/70 p-8 shadow-[0_25px_80px_rgba(0,0,0,.35)]">
              <div className="text-xs font-black uppercase tracking-[0.42em] text-cyan-200">Scout Standard</div>
              <h2 className="mt-5 text-4xl font-black tracking-[-0.06em]">What makes a strong scout?</h2>
              <p className="mt-4 max-w-3xl text-lg leading-8 text-slate-300">A strong scout does not rush to recommend. A strong scout first observes.</p>
              <div className="mt-8 grid gap-5 md:grid-cols-2">
                {[
                  "What is actually happening here?",
                  "Where is time being lost?",
                  "Where is responsibility unclear?",
                  "Where are stock numbers not trusted?",
                  "Where are orders delayed?",
                  "Where is the business busy but not improving?",
                ].map((question) => <div key={question} className="rounded-3xl border border-blue-300/20 bg-blue-400/10 p-5 font-bold text-blue-50">• {question}</div>)}
              </div>
              <div className="mt-8 rounded-3xl border border-cyan-200/20 bg-cyan-300/10 p-6 leading-8 text-cyan-50">The best scouts do not bring us the most businesses. They bring us the clearest opportunities.</div>
            </div>
          </div>
        </section>

        <section className="bg-[#050b18] px-7 pb-24 text-white sm:px-10 lg:px-12">
          <div className="mx-auto grid max-w-[1500px] gap-6 rounded-[2rem] border border-blue-400/40 bg-blue-500/10 p-8 md:grid-cols-[1fr_auto_1fr] md:items-center">
            <div className="text-4xl font-black uppercase tracking-[-0.06em]"><span className="text-white">Ready to</span><br /><span className="text-blue-400">make an impact?</span></div>
            <button type="button" onClick={() => setStep("form")} className="rounded-xl bg-blue-500 px-9 py-5 text-lg font-black text-white shadow-xl hover:bg-blue-400">Apply Now →</button>
            <p className="text-base leading-7 text-slate-300">Your role is to discover. Your future is built by the value you create.</p>
          </div>
          <div className="mx-auto mt-10 max-w-[1500px] text-center text-xs font-black uppercase tracking-[0.35em] text-slate-400">One Operation. One Truth. <span className="text-blue-400">Better Every Day.</span></div>
        </section>
      </main>
    </div>
  );
}
export default function ScoutLoginPage() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [accessLayer, setAccessLayer] = useState("scout");
  const [showApplication, setShowApplication] = useState(typeof window !== "undefined" && window.location.pathname.includes("/scouts/apply"));

  if (showApplication) return <PublicScoutApplication onBack={() => setShowApplication(false)} />;

  function submit(e) {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    const loginEmail = String(email || "").trim().toLowerCase();
    const source = accessLayer === "hq" ? getHQCredentials() : getScoutCredentials();
    const scout = source.find((row) => String(row.email || "").toLowerCase() === loginEmail && String(row.password || "") === password);
    window.setTimeout(() => {
      if (!scout) {
        setLoading(false);
        setMessage(`Access denied. Check the ${accessLayer === "hq" ? "HQ admin" : "scout"} username and password.`);
        return;
      }
      setScoutSession({ ...scout, access: accessLayer });
      nav("/scouts", { replace: true });
    }, 450);
  }

  return (
    <div className="min-h-screen overflow-hidden bg-[#061427] text-white">
      <div className="pointer-events-none fixed inset-0 opacity-80">
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,.04)_1px,transparent_1px),linear-gradient(180deg,rgba(255,255,255,.04)_1px,transparent_1px)] bg-[size:72px_72px]" />
        <div className="absolute -left-32 top-8 h-96 w-96 rounded-full bg-cyan-400/20 blur-3xl" />
        <div className="absolute right-0 top-0 h-[32rem] w-[32rem] rounded-full bg-blue-500/20 blur-3xl" />
      </div>

      <header className="relative z-10 flex items-center justify-end px-8 py-5">
        <div className="flex gap-3">
          <Link to="/login" className="rounded-full border border-white/15 px-5 py-2 text-xs font-black uppercase tracking-[0.18em] text-white/80 hover:bg-white/10">Neo Login</Link>
          <Link to="/" className="rounded-full border border-white/15 px-5 py-2 text-xs font-black uppercase tracking-[0.18em] text-white/80 hover:bg-white/10">Home</Link>
        </div>
      </header>

      <main className="relative z-10 mx-auto grid min-h-[calc(100vh-120px)] max-w-6xl grid-cols-1 items-center gap-8 px-6 pb-10 lg:grid-cols-[1fr_440px]">
        <section className="rounded-[2rem] border border-white/15 bg-white/[0.05] p-8 shadow-[0_30px_90px_rgba(0,0,0,.35)] backdrop-blur-xl">
          <div className="text-xs font-black uppercase tracking-[0.42em] text-cyan-200">Operational Scouts</div>
          <h1 className="mt-6 max-w-3xl text-5xl font-black leading-[0.95] tracking-[-0.06em]">
            <span className="block whitespace-nowrap">The future leaders aren't hired.</span>
            <span className="block bg-gradient-to-r from-cyan-300 to-blue-500 bg-clip-text text-transparent">They're discovered.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">Join a network of high-impact scouts who uncover real opportunities, build trusted relationships, and create value that drives growth.</p>
          <p className="mt-4 max-w-2xl font-black leading-8 text-cyan-100">Your insight today. Their success tomorrow. Your rewards every month.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {[
              ["Spot What Others Miss", "See the opportunity before it is obvious. You bring the eyes; MatrixOS gives you the platform."],
              ["Make a Real Impact", "Connect businesses to systems that help them grow. Your introductions can change outcomes."],
              ["Earn What You Deserve", "Get rewarded for contribution. One client can create 12 months of recurring income."],
            ].map(([title, body]) => (
              <div key={title} className="rounded-2xl border border-cyan-200/15 bg-slate-950/40 p-5">
                <div className="font-black text-cyan-100">{title}</div>
                <div className="mt-2 text-sm leading-6 text-slate-400">{body}</div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex justify-center">
            <button type="button" onClick={() => setShowApplication(true)} className="rounded-2xl bg-cyan-300 px-8 py-4 font-black text-slate-950 hover:bg-cyan-200">Apply to Become a Scout</button>
          </div>
        </section>

        <section className="rounded-[2rem] border border-white/15 bg-slate-950/70 p-7 shadow-[0_30px_90px_rgba(0,0,0,.45)] backdrop-blur-xl">
          <div className="text-xs font-black uppercase tracking-[0.36em] text-cyan-200">Access layer</div>
          <div className="mt-5 grid grid-cols-2 rounded-2xl border border-white/10 bg-white/[0.04] p-1">
            {["scout", "hq"].map((mode) => (
              <button key={mode} type="button" onClick={() => { setAccessLayer(mode); setMessage(""); }} className={`rounded-xl px-4 py-3 text-sm font-black uppercase tracking-[0.12em] ${accessLayer === mode ? "bg-cyan-300 text-slate-950" : "text-slate-300 hover:bg-white/10"}`}>
                {mode === "hq" ? "Admin" : "Scout"}
              </button>
            ))}
          </div>
          <h2 className="mt-5 text-3xl font-black tracking-[-0.04em]">{accessLayer === "hq" ? "HQ sign in" : "Scout sign in"}</h2>
          <form onSubmit={submit} className="mt-7 space-y-5">
            <div>
              <label className="mb-2 block text-xs font-black uppercase tracking-[0.24em] text-slate-300">Username</label>
              <input value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" className="w-full rounded-2xl border border-white/12 bg-slate-900/80 px-5 py-4 outline-none focus:border-cyan-300" required />
            </div>
            <div>
              <label className="mb-2 block text-xs font-black uppercase tracking-[0.24em] text-slate-300">Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded-2xl border border-white/12 bg-slate-900/80 px-5 py-4 outline-none focus:border-cyan-300" required />
            </div>
            {message ? <div className="rounded-2xl border border-amber-300/20 bg-amber-400/10 px-4 py-3 text-sm text-amber-100">{message}</div> : null}
            <button disabled={loading} className="w-full rounded-2xl bg-gradient-to-r from-cyan-300 via-sky-400 to-blue-500 px-6 py-4 font-black text-slate-950 disabled:opacity-60">{loading ? "Opening portal..." : accessLayer === "hq" ? "Enter HQ Portal" : "Enter Scout Portal"}</button>
          </form>
        </section>
      </main>
    </div>
  );
}
